import React, { useReducer, useEffect, useMemo, useCallback } from 'react';
import { Session, User, Message, Payload, ID, IDMap, Hub, Kind } from '@amtrav/chat-stream';

interface ChatWidgetState {
  session: Session;
  me?: User;
  allUsers: IDMap<User>;
  sessionMessages: Message[];
  placeholders: string[];
}

interface ChatWidgetActions {
  sendMessage: (text: string) => Promise<Message>;
  setTyping: (isTyping: boolean) => void;
  see: (msgs: Message[]) => void;
}

interface SetHub {
  type: 'setHub';
  payload: Hub;
}

interface SetSession {
  type: 'setSession';
  payload: Session;
}

interface ClearSession {
  type: 'clearSession';
}

interface UpdateSessionMessages {
  type: 'updateSessionMessages';
  payload: {
    messages: Message[];
    onNewMessages?: (msgs: Message[]) => void;
  };
}

interface AddPlaceholderText {
  type: 'addPlaceholderText';
  payload: string;
}

interface UpdateUser {
  type: 'updateUser';
  payload: User;
}

interface UpdateMe {
  type: 'updateMe';
  payload: User;
}

interface UpdateSession {
  type: 'updateSession';
  payload: Session;
}

type ChatWidgetReducerActions =
  | SetHub
  | SetSession
  | ClearSession
  | UpdateSessionMessages
  | AddPlaceholderText
  | UpdateUser
  | UpdateMe
  | UpdateSession;

function computeNewMessages(me: User, oldList: Message[], newList: Message[]) {
  const oldIds = oldList.map((msg) => msg.id);
  return newList.filter((msg) => !oldIds.includes(msg.id)).filter((msg) => msg.senderUserId !== me.id);
}

function computeMyNewMessages(me: User, oldList: Message[], newList: Message[]) {
  const oldIds = oldList.map((msg) => msg.id);
  return newList.filter((msg) => !oldIds.includes(msg.id)).filter((msg) => msg.senderUserId === me.id);
}

function createPlaceholderMessages(senderUserId: ID, sessionId: ID, texts: string[]): Message[] {
  const now = new Date().toISOString();
  return texts.map((text, i) => ({
    id: `Placeholder::${i}`,
    created: now,
    updated: now,
    deleted: false,
    sessionId,
    senderUserId,
    delivered: false,
    seen: false,
    payload: { type: 'text', data: { message: text } },
  }));
}

export const chatWidgetReducer = (state: ChatWidgetState, action: ChatWidgetReducerActions): ChatWidgetState => {
  let remainingPlaceholders: string[] = state.placeholders;
  switch (action.type) {
    case 'setHub': {
      return {
        ...state,
      };
    }
    case 'setSession':
      return {
        ...state,
        session: action.payload,
      };
    case 'clearSession':
      return {
        ...state,
        session: null,
      };
    case 'updateSession':
      return {
        ...state,
        session: action.payload,
      };
    case 'updateMe':
      return {
        ...state,
        me: action.payload,
      };
    case 'updateUser':
      return {
        ...state,
        allUsers: {
          ...state.allUsers,
          [action.payload.id]: action.payload,
        },
      };
    case 'updateSessionMessages':
      if (state.session && state.sessionMessages) {
        const newMessages = computeNewMessages(state.me, state.sessionMessages, action.payload.messages);
        if (newMessages.length > 0 && action.payload.onNewMessages) {
          action.payload.onNewMessages(newMessages);
        }
        const myNewMessages = computeMyNewMessages(state.me, state.sessionMessages, action.payload.messages);
        const newTexts = myNewMessages
          .map((msg) => {
            const { payload } = msg;
            switch (payload.type) {
              case 'text':
                return payload.data.message;
              default:
                return null;
            }
          })
          .filter((text) => text);
        remainingPlaceholders = state.placeholders.filter((text) => !newTexts.includes(text));
      }
      return {
        ...state,
        placeholders: remainingPlaceholders,
        sessionMessages: [
          ...action.payload.messages,
          ...createPlaceholderMessages(state.me?.id, state.session?.id, remainingPlaceholders),
        ],
      };
    case 'addPlaceholderText':
      return {
        ...state,
        placeholders: [...state.placeholders, action.payload],
        sessionMessages: [
          ...state.sessionMessages,
          ...createPlaceholderMessages(state.me?.id, state.session?.id, [...state.placeholders, action.payload]),
        ],
      };
    default:
      console.warn('Unknown action sent to chat reducer: ', action);
      return state;
  }
};

const makeInitialState = (session: Session, me: User): ChatWidgetState => {
  return {
    allUsers: {},
    sessionMessages: [],
    placeholders: [],
    session,
    me,
  };
};

export const useChatWidgetReducer = (
  hub: Hub,
  session: Session,
  me: User,
  onNewMessages?: (msgs: Message[]) => void,
): [ChatWidgetState, ChatWidgetActions] => {
  // We use a callback as the reducer because we need to avoid creating a new reducer on each hook call,
  // but we do need it to track the current hub.
  const [output, dispatch] = useReducer(chatWidgetReducer, makeInitialState(session, me));

  useEffect(() => {
    return hub.subscribeMe((newMe) => dispatch({ type: 'updateMe', payload: newMe }));
  }, [hub]);

  useEffect(() => {
    return hub.subscribe(Kind.SESSION, session.id, (sesUpdate) =>
      dispatch({ type: 'updateSession', payload: sesUpdate }),
    );
  }, [hub, session.id]);

  useEffect(() => {
    return hub.subSessionMessages(session.id, (messages) => {
      dispatch({ type: 'updateSessionMessages', payload: { messages, onNewMessages } });
    });
  }, [hub, session.id]);

  const allUserIds = Array.from(new Set(output.sessionMessages.map((msg) => msg.senderUserId))).sort();
  useEffect(() => {
    if (hub) {
      allUserIds.map((id) =>
        hub
          .fetch({ entityName: Kind.USER, filter: `id = ${id}` })
          .then((user) => dispatch({ type: 'updateUser', payload: user[0] as User })),
      );
    }
  }, [hub, allUserIds.join('::')]);

  const actions = useMemo(() => {
    return {
      sendMessage: (text: string) => {
        dispatch({ type: 'addPlaceholderText', payload: text });
        return hub.sendTextMessage(session.id, text);
      },
      see: (msgs: Message[]) => {
        msgs
          .filter((msg) => !msg.seen)
          .map((msg) => msg.id)
          .forEach(hub.seeMessage);
      },
      setTyping: (isTyping: boolean) => hub.update(Kind.SESSION, { id: session.id, clientTyping: isTyping }),
    };
  }, [hub]);

  return [output, actions];
};
